// src/pages/EditRentalAccount.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getBankAccount, updateBankAccount, getBankAccountTransactions, uploadTransactions } from '../services/rentalCollectionsService';
import { AuthContext } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';
import PageWrapper from '../components/PageWrapper';

const EditRentalAccount = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { agencyCurrency, user, agencyCountry } = useContext(AuthContext);
  const currencySymbol = getCurrencySymbol(agencyCurrency);

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  // Account state
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [selectedCsvFile, setSelectedCsvFile] = useState(null);
  const [formData, setFormData] = useState({
    account_name: '',
    account_type: 'checking'
  });
  const [errors, setErrors] = useState({});

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    transaction_type: '',
    description: ''
  });

  useEffect(() => {
    loadAccount();
  }, [id]);

  useEffect(() => {
    if (account) {
      loadTransactions(true); // Reset transactions when account loads
    }
  }, [account]);

  const loadAccount = async () => {
    try {
      setLoading(true);
      const accountData = await getBankAccount(id);

      setAccount(accountData);
      setFormData({
        account_name: accountData.account_name || accountData.account_holder || '',
        account_type: accountData.account_type || 'checking'
      });
    } catch (error) {
      console.error('Error loading account:', error);
      if (error.message.includes('404') || error.message.includes('not found')) {
        navigate('/bankList');
      } else {
        alert('Error loading account details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = useCallback(async (reset = false, cursor = null) => {
    try {
      if (reset) {
        setTransactionsLoading(true);
        setTransactions([]);
        setNextCursor(null);
        setHasMoreTransactions(false);
      } else {
        setLoadingMore(true);
      }

      // Build search parameters
      const searchParams = {
        limit: 50,
        ...(cursor && { cursor }),
        ...(filters.date_from && { date_from: new Date(filters.date_from).toISOString() }),
        ...(filters.date_to && { date_to: new Date(filters.date_to + 'T23:59:59').toISOString() }),
        ...(filters.transaction_type && { transaction_type: filters.transaction_type }),
        ...(filters.description && { description: filters.description })
      };

      const response = await getBankAccountTransactions(id, searchParams);

      if (reset) {
        setTransactions(response.data || []);
      } else {
        setTransactions(prev => [...prev, ...(response.data || [])]);
      }

      setNextCursor(response.next_cursor || null);
      setHasMoreTransactions(!!response.next_cursor);

    } catch (error) {
      console.error('Error loading transactions:', error);
      alert('Error loading transactions. Please try again.');
    } finally {
      setTransactionsLoading(false);
      setLoadingMore(false);
    }
  }, [id, filters]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear field-specific errors
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = () => {
    loadTransactions(true);
  };

  const handleLoadMore = () => {
    if (nextCursor && !loadingMore) {
      loadTransactions(false, nextCursor);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.account_name.trim()) {
      newErrors.account_name = 'Account name is required';
    }

    if (!formData.account_type.trim()) {
      newErrors.account_type = 'Account type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);
      await updateBankAccount(id, formData);
      alert('Account updated successfully!');
      loadAccount(); // Reload account data
    } catch (error) {
      console.error('Error updating account:', error);
      alert('Error updating account. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!selectedCsvFile) return;

    try {
      setUploadingCsv(true);
      await uploadTransactions(id, selectedCsvFile);
      alert('Transactions uploaded successfully!');
      loadTransactions(true); // Reload transactions
      setSelectedCsvFile(null); // Clear selected file
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Error uploading transactions. Please check the file format and try again.');
    } finally {
      setUploadingCsv(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return `${currencySymbol}0.00`;
    return `${currencySymbol}${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };


  if (loading) {
    return (
      <PageWrapper>
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-3"></div>
            <p className="text-gray-500">Loading account details...</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (!account) {
    return (
      <PageWrapper>
        <div className="text-center py-20">
          <h3 className="text-gray-500 text-xl mb-4">Account not found</h3>
          <Link to="/bankList" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Accounts
          </Link>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Bank Account</h1>
              <nav className="flex" aria-label="Breadcrumb">
                <ol className="inline-flex items-center space-x-1 md:space-x-3">
                  <li className="inline-flex items-center">
                    <Link to="/bankList" className="text-gray-700 hover:text-blue-600 transition-colors duration-200">
                      Bank Accounts
                    </Link>
                  </li>
                  <li>
                    <div className="flex items-center">
                      <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-500 ml-1 md:ml-2">{formData.account_name || 'Edit Account'}</span>
                    </div>
                  </li>
                </ol>
              </nav>
            </div>
            <Link to="/bankList" className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Accounts
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Account Details Form */}
          <div className="lg:col-span-1">
            <div className="bg-white border-0 shadow-sm rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h5 className="text-lg font-semibold text-gray-900 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                  Account Details
                </h5>
              </div>
              <div className="p-6">
                {/* Read-only Account Information */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h6 className="mb-4 text-gray-600 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Account Information
                  </h6>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="font-medium text-gray-900">Account Holder:</div>
                      <div className="text-gray-500">{account.account_holder || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Bank Name:</div>
                      <div className="text-gray-500">{account.bank_name || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Account Number:</div>
                      <div className="text-gray-500">{account.account_number || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Current Balance:</div>
                      <div className="text-gray-500">
                        {account.balance ? formatCurrency(account.balance.amount) : 'N/A'}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    These fields are read-only and cannot be modified through this interface.
                  </p>
                </div>

                <form onSubmit={handleSave}>
                  <div className="mb-4">
                    <label htmlFor="account_name" className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
                    <input
                      type="text"
                      className={`block w-full px-3 py-2 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 ${
                        errors.account_name 
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:border-blue-500'
                      }`}
                      id="account_name"
                      name="account_name"
                      value={formData.account_name}
                      onChange={handleInputChange}
                      placeholder="Enter account name"
                    />
                    {errors.account_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.account_name}</p>
                    )}
                  </div>

                  <div className="mb-6">
                    <label htmlFor="account_type" className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
                    <select
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                      id="account_type"
                      name="account_type"
                      value={formData.account_type}
                      onChange={handleInputChange}
                    >
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                      <option value="business">Business</option>
                    </select>
                  </div>

                  <div className="w-full">
                    <button
                      type="submit"
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg me-2"></i>
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* CSV Upload Section */}
                <hr className="my-4" />
                <div>
                  <h6 className="mb-3">
                    <i className="bi bi-upload me-2"></i>
                    Upload Transactions
                  </h6>
                  <div className="mb-2">
                    <input
                      type="file"
                      className="form-control"
                      accept=".csv"
                      onChange={(e) => setSelectedCsvFile(e.target.files[0])}
                      disabled={uploadingCsv}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCsvUpload}
                    disabled={uploadingCsv || !selectedCsvFile}
                  >
                    {uploadingCsv ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-upload me-2"></i>
                        Upload CSV
                      </>
                    )}
                  </button>
                  <small className="text-muted d-block mt-2">
                    Upload a CSV file with transaction data. Max file size: 10MB
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Transactions */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-list-ul me-2"></i>
                    Transactions
                  </h5>
                  <span className="badge bg-primary">
                    {transactions.length} transactions
                  </span>
                </div>
              </div>

              {/* Search Filters */}
              <div className="card-body border-bottom">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="date_from" className="form-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="date_from"
                      name="date_from"
                      value={filters.date_from}
                      onChange={handleFilterChange}
                      onKeyDown={(e) => e.preventDefault()}
                    />
                    {filters.date_from && (
                      <small className="form-text fw-semibold text-primary">
                        {formatDateWithContext(filters.date_from)}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="date_to" className="form-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="date_to"
                      name="date_to"
                      value={filters.date_to}
                      onChange={handleFilterChange}
                      onKeyDown={(e) => e.preventDefault()}
                    />
                    {filters.date_to && (
                      <small className="form-text fw-semibold text-primary">
                        {formatDateWithContext(filters.date_to)}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="transaction_type" className="form-label">Transaction Type</label>
                    <select
                      className="form-select"
                      id="transaction_type"
                      name="transaction_type"
                      value={filters.transaction_type}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Types</option>
                      <option value="credit">Credit</option>
                      <option value="debit">Debit</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="description" className="form-label">Description Contains</label>
                    <input
                      type="text"
                      className="form-control"
                      id="description"
                      name="description"
                      value={filters.description}
                      onChange={handleFilterChange}
                      placeholder="Search description..."
                    />
                  </div>
                  <div className="col-12">
                    <button
                      type="button"
                      className="btn btn-primary me-2"
                      onClick={handleSearch}
                      disabled={transactionsLoading}
                    >
                      {transactionsLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Searching...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-search me-2"></i>
                          Search
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setFilters({ date_from: '', date_to: '', transaction_type: '', description: '' });
                        setTimeout(() => loadTransactions(true), 0);
                      }}
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              {/* Transactions List */}
              <div className="card-body p-0">
                {transactionsLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3"></div>
                    <p className="text-muted">Loading transactions...</p>
                  </div>
                ) : transactions.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>Date</th>
                            <th>Description</th>
                            <th>Type</th>
                            <th className="text-end">Amount</th>
                            <th className="text-end">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((transaction, index) => (
                            <tr key={transaction.ID || index}>
                              <td>{formatDateWithContext(transaction.TransactionDate)}</td>
                              <td>
                                <div className="fw-medium">{transaction.Description || '-'}</div>
                                {transaction.category && (
                                  <small className="text-muted">{transaction.category}</small>
                                )}
                              </td>
                              <td>
                                <span className={`badge ${
                                  transaction.TransactionType === 'credit' ? 'bg-success' : 'bg-danger'
                                }`}>
                                  {transaction.TransactionType || 'N/A'}
                                </span>
                              </td>
                              <td className="text-end">
                                <span className={
                                  transaction.TransactionType === 'credit' ? 'text-success' : 'text-danger'
                                }>
                                  {transaction.TransactionType === 'credit' ? '+' : '-'}
                                  {transaction.Amount ? transaction.Amount.formatted : formatCurrency(0)}
                                </span>
                              </td>
                              <td className="text-end font-monospace">
                                {transaction.balance ? formatCurrency(transaction.balance) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Load More Button */}
                    {hasMoreTransactions && (
                      <div className="text-center p-3 border-top">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={handleLoadMore}
                          disabled={loadingMore}
                        >
                          {loadingMore ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Loading more...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-arrow-down me-2"></i>
                              Load More Transactions
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="bi bi-inbox display-4 text-muted"></i>
                    </div>
                    <h5 className="text-muted">No Transactions Found</h5>
                    <p className="text-muted">
                      {Object.values(filters).some(f => f) ?
                        'No transactions match your search criteria.' :
                        'This account has no transactions yet.'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default EditRentalAccount;
